import { useParams } from 'react-router-dom'
import { getPlanUrl } from '@domainUtils'
import { AxiosError } from 'axios'
import { Delete, Download, FileCopy, OpenInNew } from '@mui/icons-material'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { TooltipIconButton } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { PDFAConformance } from '@editorComponents'
import { deletePlan, savePlan } from '@mutations'
import { buildErrorMessage } from 'src/constants'
import ConvertButton from '../ConvertButton'

interface Props {
  plan: Plan
  onDelete: () => void
  onDuplicate: (plan: Plan) => void
  getBlobPDFA: (
    pdfUlr: string,
    conformance: PDFAConformance,
  ) => Promise<{ blob: Blob; errors: string[] } | null>
}

const PlanActionCell = ({ plan, onDelete, onDuplicate, getBlobPDFA }: Props) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const { mutate, isLoading } = useMutation(
    () => deletePlan.request(projectId as string, plan.guid),
    {
      onSuccess: () => {
        enqueueSnackbar('Plan erfolgreich gelöscht', { variant: 'success' })
        onDelete()
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Plans'), {
          variant: 'error',
        })
      },
    },
  )

  const planUrl = getPlanUrl(plan, projectId as string)

  const { mutate: duplicatePlan, isLoading: isLoadingDuplicate } = useMutation(
    async () => {
      // Download the file as a blob
      const response = await fetch(planUrl)
      if (!response.ok) throw new Error('File download failed')
      const blob = await response.blob()
      // Optionally, create a File object if you need a filename
      const file = new File([blob], plan.node_name || 'plan.pdf', { type: blob.type })

      const data = {
        title: `${plan.node_name} (Kopie)`,
        comment: plan.comment,
        file,
      }
      return savePlan.request(projectId as string, data)
    },
    {
      onSuccess: (plan: Plan) => {
        enqueueSnackbar('Plan erfolgreich dupliziert', { variant: 'success' })
        onDuplicate(plan)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Duplizieren des Plans'), {
          variant: 'error',
        })
      },
    },
  )

  const downloadLink = getPlanUrl(plan, projectId as string)

  return (
    <Stack
      direction="row"
      spacing={0}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ '& .MuiIconButton-root': { padding: '4px' } }}
    >
      {isLoadingDuplicate ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }} pr={0.5}>
          <CircularProgress size={12} />
        </Box>
      ) : (
        <TooltipIconButton
          tooltip="Plan duplizieren"
          onClick={event => {
            event.stopPropagation()
            duplicatePlan()
          }}
        >
          <FileCopy fontSize="small" />
        </TooltipIconButton>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }} pr={0.5}>
          <CircularProgress size={12} />
        </Box>
      ) : (
        <TooltipIconButton
          tooltip="Plan löschen"
          onClick={event => {
            event.stopPropagation()
            mutate()
          }}
        >
          <Delete fontSize="small" />
        </TooltipIconButton>
      )}

      <TooltipIconButton
        tooltip="Plan in neuem Tab öffnen"
        onClick={event => {
          event.stopPropagation()
        }}
        href={`/plans/${projectId}/${plan.guid}`}
        target="_blank"
      >
        <OpenInNew fontSize="small" />
      </TooltipIconButton>

      <TooltipIconButton
        tooltip="Plan herunterladen"
        href={downloadLink}
        target="_blank"
        component="a"
      >
        <Download fontSize="small" />
      </TooltipIconButton>

      <ConvertButton plan={plan} getBlobPDFA={getBlobPDFA} />
    </Stack>
  )
}

export default PlanActionCell
